import { createContext, useContext } from 'react';

/** Public BigChange worksheet and attachment objects. No other host is fetched. */
const PHOTO_HOST = 's3-eu-west-1.amazonaws.com';
const PHOTO_PATH_PREFIX = '/bigchange/Attachment/';
const TRUNCATION_NOTE = /\n\n… truncated [\d,]+ characters for display$/;
const PHOTO_URL_RE =
  /https:\/\/s3-eu-west-1\.amazonaws\.com\/bigchange\/Attachment\/[^\s"'<>\\]+/g;
const GENERIC_PHOTO_LABEL =
  /^(view\s+)?(the\s+)?(equipment\s+|worksheet\s+)?photo$/i;
const PLACEHOLDER_ANSWER =
  /^\[(photo|attachment)( not loaded| unavailable)?\]$/i;

const MAX_PHOTOS = 24;
const MAX_DEPTH = 8;
const LABEL_MAX = 180;

export const VISIBLE_PHOTO_COUNT = 6;

const IMAGE_EXT = /\.(jpe?g|png|gif|webp)$/i;
const PDF_EXT = /\.pdf$/i;

export type AttachmentKind = 'image' | 'pdf' | 'file';

export type WorksheetPhoto = {
  url: string;
  thumbnailUrl: string;
  label?: string;
};

export type AttachmentFile = {
  url: string;
  kind: 'pdf' | 'file';
  label?: string;
};

export type CollectedAttachments = {
  photos: WorksheetPhoto[];
  files: AttachmentFile[];
};

export type WorksheetPhotoContextValue = {
  photos: WorksheetPhoto[];
  files: AttachmentFile[];
  openPhoto: (photo: WorksheetPhoto) => void;
  openAt: (index: number) => void;
  registerPhoto: (photo: WorksheetPhoto) => void;
};

export const WorksheetPhotoContext =
  createContext<WorksheetPhotoContextValue | null>(null);

export const useWorksheetPhotos = () => useContext(WorksheetPhotoContext);

export function isWorksheetPhotoUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      url.hostname === PHOTO_HOST &&
      url.pathname.startsWith(PHOTO_PATH_PREFIX) &&
      url.username === '' &&
      url.password === ''
    );
  } catch {
    return false;
  }
}

const cleanLabel = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.replace(/\s+/g, ' ').trim();
  if (!trimmed) return undefined;
  if (GENERIC_PHOTO_LABEL.test(trimmed)) return undefined;
  if (PLACEHOLDER_ANSWER.test(trimmed)) return undefined;
  if (isWorksheetPhotoUrl(trimmed)) return undefined;
  if (trimmed.length <= LABEL_MAX) return trimmed;
  return `${trimmed.slice(0, LABEL_MAX - 1)}…`;
};

const photoKey = (value: string): string => {
  try {
    return new URL(value).pathname.replace('/thumb_pic_', '/pic_');
  } catch {
    return value;
  }
};

const toPair = (
  url: string,
  thumbnailUrl?: string
): { url: string; thumbnailUrl: string } => {
  const full = url.includes('/thumb_pic_')
    ? url.replace('/thumb_pic_', '/pic_')
    : url;
  const thumb =
    thumbnailUrl && isWorksheetPhotoUrl(thumbnailUrl)
      ? thumbnailUrl
      : full.includes('/pic_')
        ? full.replace('/pic_', '/thumb_pic_')
        : full;
  return { url: full, thumbnailUrl: thumb };
};

/** Image, PDF, or other file on the BigChange attachment host. Anything else is ignored. */
export function attachmentKind(value: string): AttachmentKind | null {
  if (!isWorksheetPhotoUrl(value)) return null;
  let path = value;
  try {
    path = new URL(value).pathname;
  } catch {
    return null;
  }
  if (
    path.includes('/pic_') ||
    path.includes('/thumb_pic_') ||
    IMAGE_EXT.test(path)
  ) {
    return 'image';
  }
  if (PDF_EXT.test(path)) return 'pdf';
  return 'file';
}

export function worksheetPhotoFromUrl(
  value: string,
  label?: string
): WorksheetPhoto | null {
  if (attachmentKind(value) !== 'image') return null;
  return { ...toPair(value), label: cleanLabel(label) };
}

const fileFromUrl = (value: string, label?: string): AttachmentFile | null => {
  const kind = attachmentKind(value);
  if (kind !== 'pdf' && kind !== 'file') return null;
  return { url: value, kind, label: cleanLabel(label) };
};

const pushPhoto = (
  found: WorksheetPhoto[],
  seen: Set<string>,
  url: string,
  thumbnailUrl: string | undefined,
  label: string | undefined
) => {
  if (found.length >= MAX_PHOTOS || attachmentKind(url) !== 'image') return;
  const pair = toPair(url, thumbnailUrl);
  const key = photoKey(pair.url);
  if (seen.has(key)) return;
  seen.add(key);
  found.push({ ...pair, label: cleanLabel(label) });
};

const pushFile = (
  found: AttachmentFile[],
  seen: Set<string>,
  url: string,
  label: string | undefined
) => {
  if (found.length >= MAX_PHOTOS) return;
  const file = fileFromUrl(url, label);
  if (!file) return;
  const key = photoKey(file.url);
  if (seen.has(key)) return;
  seen.add(key);
  found.push(file);
};

const addLink = (
  photos: WorksheetPhoto[],
  photoSeen: Set<string>,
  files: AttachmentFile[],
  fileSeen: Set<string>,
  url: string,
  thumbnailUrl: string | undefined,
  label: string | undefined
) => {
  if (attachmentKind(url) === 'image') {
    pushPhoto(photos, photoSeen, url, thumbnailUrl, label);
    return;
  }
  pushFile(files, fileSeen, url, label);
};

const photosFromText = (
  text: string,
  photos: WorksheetPhoto[],
  photoSeen: Set<string>,
  files: AttachmentFile[],
  fileSeen: Set<string>
) => {
  const matches = text.match(PHOTO_URL_RE) ?? [];
  for (const match of matches) {
    const clean = match.replace(/[),.;]+$/g, '');
    if (!isWorksheetPhotoUrl(clean)) continue;
    const thumbnailUrl = clean.includes('/thumb_pic_') ? clean : undefined;
    const url = thumbnailUrl ? clean.replace('/thumb_pic_', '/pic_') : clean;
    addLink(photos, photoSeen, files, fileSeen, url, thumbnailUrl, undefined);
  }
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const recordLabel = (
  record: Record<string, unknown>,
  inheritedLabel: string | undefined
) =>
  cleanLabel(
    typeof record.question === 'string' ? record.question : undefined
  ) ??
  cleanLabel(
    typeof record.fileName === 'string' ? record.fileName : undefined
  ) ??
  inheritedLabel;

const linkUrl = (record: Record<string, unknown>): string | undefined => {
  if (typeof record.url === 'string') return record.url;
  if (typeof record.link === 'string' && isWorksheetPhotoUrl(record.link)) {
    return record.link;
  }
  return undefined;
};

const visit = (
  value: unknown,
  depth: number,
  inheritedLabel: string | undefined,
  photos: WorksheetPhoto[],
  photoSeen: Set<string>,
  files: AttachmentFile[],
  fileSeen: Set<string>
) => {
  if (
    depth > MAX_DEPTH ||
    (photos.length >= MAX_PHOTOS && files.length >= MAX_PHOTOS)
  ) {
    return;
  }

  if (typeof value === 'string') {
    addLink(
      photos,
      photoSeen,
      files,
      fileSeen,
      value,
      undefined,
      inheritedLabel
    );
    return;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      visit(
        item,
        depth + 1,
        inheritedLabel,
        photos,
        photoSeen,
        files,
        fileSeen
      );
    }
    return;
  }

  const record = asRecord(value);
  if (!record) return;

  const label = recordLabel(record, inheritedLabel);
  const thumbnailUrl =
    typeof record.thumbnailUrl === 'string' ? record.thumbnailUrl : undefined;

  // Worksheet answers carry the question and a photos list. Get Attachment
  // is a single url, which may be a PDF rather than an image.
  if (Array.isArray(record.photos)) {
    for (const item of record.photos) {
      const photo = asRecord(item);
      const url = photo ? linkUrl(photo) : undefined;
      if (!url) continue;
      const thumb =
        photo && typeof photo.thumbnailUrl === 'string'
          ? photo.thumbnailUrl
          : undefined;
      addLink(photos, photoSeen, files, fileSeen, url, thumb, label);
    }
  } else {
    const url = linkUrl(record);
    if (url) {
      addLink(photos, photoSeen, files, fileSeen, url, thumbnailUrl, label);
    }
  }

  for (const [key, nested] of Object.entries(record)) {
    if (
      key === 'url' ||
      key === 'link' ||
      key === 'thumbnailUrl' ||
      key === 'photos'
    ) {
      continue;
    }
    if (nested !== null && typeof nested === 'object') {
      visit(nested, depth + 1, label, photos, photoSeen, files, fileSeen);
    }
  }
};

const parseOutput = (output: unknown): unknown => {
  if (typeof output !== 'string') return output;
  const trimmed = output.replace(TRUNCATION_NOTE, '').trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
};

/** Images and other files from worksheet answers and get_attachment results. */
export function collectAttachmentMedia(
  outputs: { output: unknown }[] | undefined
): CollectedAttachments {
  const photos: WorksheetPhoto[] = [];
  const files: AttachmentFile[] = [];
  const photoSeen = new Set<string>();
  const fileSeen = new Set<string>();

  for (const output of outputs ?? []) {
    const parsed = parseOutput(output.output);
    if (typeof parsed === 'string') {
      photosFromText(parsed, photos, photoSeen, files, fileSeen);
      continue;
    }
    visit(parsed, 0, undefined, photos, photoSeen, files, fileSeen);
  }

  return { photos, files };
}
